/**
 * In-memory + sessionStorage log for service-worker / cache cleanup and
 * build-version checks. Surfaced on the /diagnostics screen.
 */

export type CacheLogLevel = "info" | "warn" | "error";

export interface CacheLogEntry {
  ts: number;
  level: CacheLogLevel;
  source: "sw-cleanup" | "build-version" | "diagnostics";
  message: string;
  detail?: unknown;
}

const STORAGE_KEY = "cache-cleanup-log";
const MAX_ENTRIES = 200;

type Listener = (entries: CacheLogEntry[]) => void;
const listeners = new Set<Listener>();

function read(): CacheLogEntry[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as CacheLogEntry[]) : [];
  } catch {
    return [];
  }
}

function write(entries: CacheLogEntry[]): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  } catch {
    /* ignore quota */
  }
}

export function logCacheEvent(
  source: CacheLogEntry["source"],
  level: CacheLogLevel,
  message: string,
  detail?: unknown,
): void {
  const entry: CacheLogEntry = { ts: Date.now(), level, source, message, detail };
  const next = [...read(), entry].slice(-MAX_ENTRIES);
  write(next);

  const tag = `[${source}]`;
  if (level === "error") console.error(tag, message, detail ?? "");
  else if (level === "warn") console.warn(tag, message, detail ?? "");
  else console.info(tag, message, detail ?? "");

  for (const l of listeners) {
    try { l(next); } catch { /* ignore */ }
  }
}

export function getCacheLog(): CacheLogEntry[] {
  return read();
}

export function clearCacheLog(): void {
  write([]);
  for (const l of listeners) {
    try { l([]); } catch { /* ignore */ }
  }
}

export function subscribeCacheLog(listener: Listener): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}
